import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Lightning, XCircle } from "@phosphor-icons/react";

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [plan, setPlan] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const checkStatus = async () => {
      try {
        // Try new recurring subscription endpoint first
        let res;
        try {
          res = await axios.get(`${API}/subscriptions/recurring-status/${sessionId}`);
          if (res.data.active) {
            setStatus("success");
            setPlan(res.data.tier);
            await refreshUser();
            return;
          }
        } catch (e) {
          // Fall back to legacy endpoint for one-time payments still in flight
          res = await axios.get(`${API}/subscriptions/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            setStatus("success");
            setPlan(res.data.plan);
            await refreshUser();
            return;
          }
        }

        if (res?.data?.status === "expired") {
          setStatus("failed");
        } else if (attempts < 5) {
          setAttempts(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          setStatus("failed");
        }
      } catch (error) {
        if (attempts < 5) {
          setAttempts(prev => prev + 1);
          setTimeout(checkStatus, 2000);
        } else {
          setStatus("failed");
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts, refreshUser]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <Card className="bg-zinc-950 border-zinc-800 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <div className="loading-spinner mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Processing Payment</h2>
              <p className="text-zinc-400 text-sm">Please wait while we confirm your subscription...</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle weight="fill" className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Welcome to {plan?.charAt(0).toUpperCase() + plan?.slice(1)}!</h2>
              <p className="text-zinc-400 mb-6">Your subscription is now active. Enjoy all the premium features!</p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate("/grants")}
                  className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                  data-testid="browse-grants-btn"
                >
                  <Lightning className="w-4 h-4 mr-2" />
                  Start Using Flash Drafts
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="w-full border-zinc-700"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {status === "failed" && (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle weight="fill" className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-xl font-bold mb-2">Payment Failed</h2>
              <p className="text-zinc-400 mb-6">We couldn't process your payment. Please try again.</p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate("/pricing")}
                  className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                  data-testid="try-again-btn"
                >
                  Try Again
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="w-full border-zinc-700"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
